import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { CricketScoring, GameType, MatchConfig, TeamPlayMode } from "@shared/index";
import {
  DEFAULT_AUTODARTS_URL,
  STANDARD_CRICKET_NUMBERS,
  TEAM_COUNT_OPTIONS,
  X01_PRESETS,
  applyThrowSequence,
  defaultMatchLength,
  defaultTeamFirstThrower,
  defaultThrowSequence,
  fallbackTeamLabel,
  isTeamFormat,
  playingTeamCount,
  randomCricketSet,
  sideOf,
  supportsTeamPlay,
  teamSlotCount,
  toMatchPlayer,
  soloFirstThrowerForCount,
  usesThrowOrderPopup,
} from "@shared/index";
import JoinQr from "../components/JoinQr";
import ThrowOrderPicker from "../components/ThrowOrderPicker";
import { CRICKET_TEAM_PLAY_BLURBS, GAME_BLURBS, GAME_LABELS, TEAM_PLAY_BLURBS, TEAM_PLAY_LABELS } from "../lib/labels";
import { createPlayer, fetchPlayers, fetchTeams, type RegisteredPlayer, type TeamTree } from "../lib/statsApi";

const GAMES: GameType[] = ["x01", "cricket", "elimination", "clock", "shanghai", "highscore"];

interface Props {
  config: MatchConfig;
  code: string;
  offline?: boolean;
  lanUrls: string[];
  joinUrl?: string | null;
  onChange: (config: MatchConfig) => void;
  onStart: () => void;
  onHome?: () => void;
  onJoinOther?: (code: string) => void;
  onChangeMode?: () => void;
  apiBase?: string;
  origin?: string | null;
  error?: string | null;
}

function Chip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-touch rounded-2xl px-4 text-sm font-bold ${
        active ? "bg-amber-glow text-ink-950" : "bg-ink-700 text-slate-200"
      }`}
    >
      {children}
    </button>
  );
}

function selectedTeamIds(playingTeams: { id: string }[] | undefined | null): Set<string> {
  return new Set((playingTeams ?? []).map((t) => t.id).filter(Boolean));
}

/** Local/unassigned when no team is picked; otherwise only members of the selected teams. */
function playerFitsTeamSelection(
  player: { teamId?: string | null },
  teamIds: ReadonlySet<string>,
): boolean {
  if (teamIds.size === 0) return !player.teamId;
  return Boolean(player.teamId && teamIds.has(player.teamId));
}

function pickerGroups(
  registry: RegisteredPlayer[],
  teams: TeamTree[] | undefined | null,
  playingTeams: { id: string; name: string }[],
  used: Set<string>,
  currentId: string,
): { label: string; players: RegisteredPlayer[] }[] {
  const available = (list: RegisteredPlayer[]) =>
    list.filter((pl) => !used.has(pl.id) || pl.id === currentId);
  const teamList = teams ?? [];

  if (!playingTeams.length) {
    return [{ label: "", players: available(registry.filter((pl) => !pl.teamId)) }];
  }

  const selected = teamList.filter((team) => playingTeams.some((t) => t.id === team.id));
  if (selected.length > 0) {
    return selected.map((team) => ({
      label: team.name,
      players: available(team.players ?? []),
    }));
  }

  // Teams failed to load or list is empty — still offer registry members of the chosen ids.
  return playingTeams.map((team) => ({
    label: team.name,
    players: available(registry.filter((pl) => pl.teamId === team.id)),
  }));
}

function teamSideStarts(firstThrowerIndex: number, side: number): boolean {
  return sideOf(firstThrowerIndex) === side;
}

function PlayerSlot({
  index,
  config,
  players,
  registry,
  teams,
  playingTeams,
  onPick,
  onFirst,
}: {
  index: number;
  config: MatchConfig;
  players: MatchConfig["players"];
  registry: RegisteredPlayer[];
  teams: TeamTree[];
  playingTeams: { id: string; name: string }[];
  onPick: (index: number, id: string) => void;
  onFirst?: () => void;
}) {
  const p = players[index];
  if (!p) return null;
  const used = new Set(players.filter((_, idx) => idx !== index).map((x) => x.id).filter(Boolean));
  const slotTeams =
    isTeamFormat(config) && playingTeams.length === playingTeamCount(config)
      ? [playingTeams[sideOf(index)]!]
      : playingTeams;
  const grouped = pickerGroups(registry, teams, slotTeams, used, p.id);
  return (
    <div className="flex items-center gap-3">
      <select
        value={p.id}
        onChange={(e) => onPick(index, e.target.value)}
        className="min-h-touch flex-1 rounded-2xl bg-ink-950 px-4 outline-none ring-amber-glow/30 focus:ring"
      >
        <option value="">Spieler wählen…</option>
        {grouped.map((group) =>
          group.label ? (
            <optgroup key={group.label} label={group.label}>
              {group.players.map((pl) => (
                <option key={pl.id} value={pl.id}>
                  {pl.name}
                </option>
              ))}
            </optgroup>
          ) : (
            group.players.map((pl) => (
              <option key={pl.id} value={pl.id}>
                {pl.name}
                {pl.teamName ? ` · ${pl.teamName}` : ""}
              </option>
            ))
          ),
        )}
      </select>
      {onFirst ? (
        <Chip active={(config.firstThrowerIndex ?? 0) === index} onClick={onFirst}>
          Wirft zuerst
        </Chip>
      ) : null}
    </div>
  );
}

export default function Setup({
  config,
  code,
  offline,
  lanUrls,
  joinUrl,
  onChange,
  onStart,
  onHome,
  onJoinOther,
  onChangeMode,
  apiBase = "",
  origin = null,
  error = null,
}: Props) {
  const players = config.players ?? [];
  const n = players.length;
  const playingTeams = config.teams ?? [];
  const desktop = window.steeldartDesktop;
  const [registry, setRegistry] = useState<RegisteredPlayer[]>([]);
  const [teams, setTeams] = useState<TeamTree[]>([]);
  const [newName, setNewName] = useState("");
  const [newPassNr, setNewPassNr] = useState("");
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [otherCode, setOtherCode] = useState("");
  const [orderOpen, setOrderOpen] = useState(false);
  const [startSequence, setStartSequence] = useState<number[]>([]);
  const popupOrder = usesThrowOrderPopup(config.gameType);
  const teamsSelected = playingTeams.length > 0;
  const registryIds = new Set(registry.map((p) => p.id));
  const ready = teamsSelected
    ? players.every((p) => Boolean(p.id && registryIds.has(p.id) && p.name.trim()))
    : true;
  const onChangeRef = useRef(onChange);
  const configRef = useRef(config);
  onChangeRef.current = onChange;
  configRef.current = config;

  useEffect(() => {
    if (!desktop?.getState) return;
    void desktop.getState().then((state) => {
      const saved = (state.autodartsUrl ?? "").trim();
      if (!saved) return;
      const current = (configRef.current.autodartsUrl ?? "").trim();
      if (current && current !== DEFAULT_AUTODARTS_URL) return;
      onChangeRef.current({ ...configRef.current, autodartsUrl: saved });
    });
  }, [desktop]);

  useEffect(() => {
    void fetchPlayers(apiBase, desktop, origin)
      .then((players) => {
        setRegistry(players);
        void fetchTeams(apiBase, desktop, origin)
          .then(setTeams)
          .catch((err) => {
            setTeams([]);
            setPlayerError(err instanceof Error ? err.message : "Teams konnten nicht geladen werden.");
          });
        const cfg = configRef.current;
        const slots = cfg.players ?? [];
        const ids = new Set(players.map((p) => p.id));
        if (players.length === 0) return;
        const teamIds = selectedTeamIds(cfg.teams);
        const fits = (p: RegisteredPlayer) => playerFitsTeamSelection(p, teamIds);
        const slotOk = (slot: { id: string }) => {
          if (!slot.id || !ids.has(slot.id)) return false;
          const full = players.find((x) => x.id === slot.id);
          return Boolean(full && fits(full));
        };
        const nextPlayers = slots.map((slot) =>
          slotOk(slot) ? slot : { id: "", name: "", teamId: null, teamName: null },
        );
        const changed = nextPlayers.some(
          (p, i) => p.id !== (slots[i]?.id ?? "") || p.name !== (slots[i]?.name ?? ""),
        );
        if (!changed) return;
        onChangeRef.current({ ...cfg, players: nextPlayers });
      })
      .catch((err) => {
        setRegistry([]);
        setPlayerError(err instanceof Error ? err.message : "Spieler konnten nicht geladen werden.");
      });
  }, [apiBase, origin]);

  function setPlayers(count: number) {
    const nextPlayers = Array.from({ length: count }, (_, i) => ({
      id: players[i]?.id ?? "",
      name: players[i]?.name ?? "",
    }));
    onChange({
      ...config,
      players: nextPlayers,
      firstThrowerIndex: soloFirstThrowerForCount(config.gameType, count),
      throwSequence: defaultThrowSequence(count),
    });
  }

  function setTeamPlay(mode: TeamPlayMode) {
    if (mode === "solo") {
      const nextPlayers = Array.from({ length: 2 }, (_, i) => ({
        id: players[i]?.id ?? "",
        name: players[i]?.name ?? "",
        teamId: players[i]?.teamId ?? null,
        teamName: players[i]?.teamName ?? null,
      }));
      onChange({
        ...config,
        teamPlay: "solo",
        players: nextPlayers,
        firstThrowerIndex: soloFirstThrowerForCount(config.gameType, nextPlayers.length),
      });
      return;
    }
    const teamCount = isTeamFormat(config) ? playingTeamCount(config) : 2;
    const nextPlayers = Array.from({ length: teamSlotCount(teamCount) }, (_, i) => ({
      id: players[i]?.id ?? "",
      name: players[i]?.name ?? "",
      teamId: players[i]?.teamId ?? null,
      teamName: players[i]?.teamName ?? null,
    }));
    const teamIds = selectedTeamIds(playingTeams);
    const filteredPlayers = nextPlayers.map((slot) => {
      const full = registry.find((p) => p.id === slot.id);
      const teamId = full?.teamId ?? slot.teamId;
      if (playerFitsTeamSelection({ teamId }, teamIds)) return slot;
      return { id: "", name: "", teamId: null, teamName: null };
    });
    onChange({
      ...config,
      teamPlay: mode,
      players: filteredPlayers,
      firstThrowerIndex: defaultTeamFirstThrower(teamCount),
    });
  }

  function pickPlayer(i: number, id: string) {
    const selected = registry.find((p) => p.id === id);
    const nextPlayers = players.map((p, idx) =>
      idx === i ? (selected ? toMatchPlayer(selected) : { id: "", name: "" }) : p,
    );
    onChange({ ...config, players: nextPlayers });
  }

  function setTeamsPlaying(next: { id: string; name: string }[]) {
    const teamIds = selectedTeamIds(next);
    const nextPlayers = players.map((slot) => {
      const full = registry.find((p) => p.id === slot.id);
      const teamId = full?.teamId ?? slot.teamId;
      if (playerFitsTeamSelection({ teamId }, teamIds)) return slot;
      return { id: "", name: "" };
    });
    onChange({
      ...config,
      teams: next,
      players: nextPlayers,
      firstThrowerIndex: isTeamFormat(config) ? defaultTeamFirstThrower(playingTeamCount(config)) : config.firstThrowerIndex,
    });
  }

  function setTeamCount(count: number) {
    const slots = teamSlotCount(count);
    const nextPlayers = Array.from({ length: slots }, (_, i) => ({
      id: players[i]?.id ?? "",
      name: players[i]?.name ?? "",
      teamId: players[i]?.teamId ?? null,
      teamName: players[i]?.teamName ?? null,
    }));
    onChange({
      ...config,
      players: nextPlayers,
      firstThrowerIndex: defaultTeamFirstThrower(count),
    });
  }

  function selectGame(id: GameType) {
    if (id === config.gameType) return;
    const popup = usesThrowOrderPopup(id);
    let next: MatchConfig = {
      ...config,
      gameType: id,
      ...defaultMatchLength(id),
      inputMode: id === "x01" ? "total" : "single",
      inputSource: id === "x01" ? "pad" : config.inputSource,
      teamPlay: supportsTeamPlay(id) ? config.teamPlay ?? "solo" : "solo",
    };
    if (popup) {
      const nextPlayers = Array.from({ length: 2 }, (_, i) => ({
        id: players[i]?.id ?? "",
        name: players[i]?.name ?? "",
      }));
      next = applyThrowSequence(
        {
          ...next,
          players: nextPlayers,
          firstThrowerIndex: 0,
          bullUpLastLeg: false,
        },
        defaultThrowSequence(2),
      );
    } else {
      next = {
        ...next,
        firstThrowerIndex: soloFirstThrowerForCount(id, next.players.length),
        throwSequence: defaultThrowSequence(next.players.length),
        bullUpLastLeg: id === "x01" ? config.bullUpLastLeg !== false : false,
      };
    }
    onChange(next);
  }

  function requestStart() {
    if (popupOrder && !isTeamFormat(config) && config.legsToWinSet > 1) {
      setStartSequence(defaultThrowSequence(players.length));
      setOrderOpen(true);
      return;
    }
    onStart();
  }

  const dartPad = config.inputSource !== "autodarts";

  return (
    <div className="safe-pad mx-auto min-h-[100dvh] max-w-4xl px-4 py-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {onHome && (
            <button className="text-sm text-slate-400" onClick={onHome}>
              ← Start
            </button>
          )}
          {onChangeMode && (
            <button className={`${onHome ? "ml-4" : ""} text-sm text-slate-400`} onClick={onChangeMode}>
              {onHome ? "Modus wechseln" : "← Modus wechseln"}
            </button>
          )}
          <h1 className={`${onHome || onChangeMode ? "mt-1" : ""} font-display text-4xl`}>Match einrichten</h1>
          <p className="text-slate-400">
            {offline ? (
              <>Lokales Spiel{lanUrls[0] ? ` · ${lanUrls[0]}` : ""}</>
            ) : (
              <>Raum-ID mit den Mitspielern teilen</>
            )}
          </p>
          {!offline && (
            <div className="mt-3 rounded-2xl bg-ink-800 px-4 py-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400">Raum-ID</div>
              <div className="font-mono text-4xl tracking-[0.2em] text-amber-glow">{code}</div>
            </div>
          )}
          {onJoinOther && (
            <form
              className="mt-3 flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const next = otherCode.trim().toUpperCase();
                if (next) onJoinOther(next);
              }}
            >
              <label className="min-w-0 flex-1 text-xs text-slate-500">
                Anderem Raum beitreten
                <input
                  value={otherCode}
                  onChange={(e) => setOtherCode(e.target.value.toUpperCase())}
                  placeholder="RAUM-ID"
                  maxLength={6}
                  autoCapitalize="characters"
                  className="mt-1 min-h-touch w-full rounded-2xl bg-ink-950 px-4 font-mono tracking-[0.2em] outline-none ring-amber-glow/40 focus:ring"
                />
              </label>
              <button type="submit" className="min-h-touch rounded-2xl bg-ink-700 px-4 text-sm font-bold">
                Beitreten
              </button>
            </form>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-3">
          {joinUrl && <JoinQr url={joinUrl} />}
          <button
            className="min-h-touch rounded-2xl bg-amber-glow px-6 font-bold text-ink-950 disabled:opacity-50"
            disabled={!ready}
            onClick={requestStart}
          >
            Start
          </button>
        </div>
      </div>

      <section className="mb-6">
        <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">Spiel</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {GAMES.map((id) => (
            <button
              key={id}
              onClick={() => selectGame(id)}
              className={`rounded-2xl p-4 text-left ${
                config.gameType === id ? "bg-amber-glow text-ink-950" : "bg-ink-800"
              }`}
            >
              <div className="font-display text-2xl">{GAME_LABELS[id]}</div>
              <div className={`text-xs ${config.gameType === id ? "text-ink-800" : "text-slate-400"}`}>
                {GAME_BLURBS[id]}
              </div>
            </button>
          ))}
        </div>
      </section>

      {config.gameType === "x01" && (
        <section className="mb-6 rounded-3xl bg-ink-800 p-5">
          <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">x01</h2>
          <div className="mb-2 text-sm text-slate-400">Start-Score</div>
          <div className="mb-4 flex flex-nowrap items-center gap-2">
            {X01_PRESETS.map((s) => (
              <Chip
                key={s}
                active={config.x01.startScore === s}
                onClick={() => onChange({ ...config, x01: { ...config.x01, startScore: s } })}
              >
                {s}
              </Chip>
            ))}
            <input
              type="number"
              min={2}
              max={10001}
              value={config.x01.startScore}
              aria-label="Eigener Startscore"
              title="Eigener Startscore"
              onChange={(e) => onChange({ ...config, x01: { ...config.x01, startScore: Number(e.target.value) || 501 } })}
              className="min-h-touch w-24 shrink-0 rounded-2xl bg-ink-950 px-3"
            />
          </div>
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <div className="mb-2 text-sm text-slate-400">In</div>
              <div className="flex flex-wrap gap-2">
                <Chip active={config.x01.inMode === "straight"} onClick={() => onChange({ ...config, x01: { ...config.x01, inMode: "straight" } })}>
                  Straight In
                </Chip>
                <Chip active={config.x01.inMode === "double"} onClick={() => onChange({ ...config, x01: { ...config.x01, inMode: "double" } })}>
                  Double In
                </Chip>
              </div>
            </div>
            <div>
              <div className="mb-2 text-sm text-slate-400">Out</div>
              <div className="flex flex-wrap gap-2">
                {(["straight", "double", "master"] as const).map((mode) => (
                  <Chip
                    key={mode}
                    active={config.x01.outMode === mode}
                    onClick={() => onChange({ ...config, x01: { ...config.x01, outMode: mode } })}
                  >
                    {mode === "straight" ? "Straight Out" : mode === "double" ? "Double Out" : "Master Out"}
                  </Chip>
                ))}
              </div>
            </div>
          </div>
          <h3 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">Team-Modus</h3>
          <div className="mb-3 flex flex-wrap gap-2">
            {(["solo", "league", "team"] as const).map((mode) => (
              <Chip key={mode} active={(config.teamPlay ?? "solo") === mode} onClick={() => setTeamPlay(mode)}>
                {TEAM_PLAY_LABELS[mode]}
              </Chip>
            ))}
          </div>
          <p className="text-sm text-slate-400">{TEAM_PLAY_BLURBS[config.teamPlay ?? "solo"]}</p>
        </section>
      )}

      {config.gameType === "cricket" && (
        <section className="mb-6 rounded-3xl bg-ink-800 p-5">
          <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">Cricket</h2>
          <div className="mb-4 grid gap-2">
            {(
              [
                {
                  id: "normal" as const,
                  title: "Normal",
                  blurb: "Extra-Marks zählen für dich. Höchste Punkte gewinnen.",
                },
                {
                  id: "cutThroat" as const,
                  title: "CutThroat",
                  blurb: "Extra-Marks zählen gegen offene Gegner. Niedrigste Punkte gewinnen.",
                },
                {
                  id: "noScore" as const,
                  title: "No Score",
                  blurb: "Keine Punkte. Wer alle Zahlen schließt, gewinnt.",
                },
              ] satisfies { id: CricketScoring; title: string; blurb: string }[]
            ).map((mode) => (
              <button
                key={mode.id}
                type="button"
                className={`rounded-2xl px-4 py-3 text-left ${
                  (config.cricket.scoring ?? "normal") === mode.id ? "bg-amber-glow text-ink-950" : "bg-ink-700"
                }`}
                onClick={() => onChange({ ...config, cricket: { ...config.cricket, scoring: mode.id } })}
              >
                <div className="font-bold">{mode.title}</div>
                <div
                  className={`text-xs ${
                    (config.cricket.scoring ?? "normal") === mode.id ? "text-ink-800" : "text-slate-400"
                  }`}
                >
                  {mode.blurb}
                </div>
              </button>
            ))}
          </div>
          <h3 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">Cricket-Zahlen</h3>
          <div className="mb-3 flex flex-wrap gap-2">
            <Chip
              active={
                config.cricket.includeBull &&
                STANDARD_CRICKET_NUMBERS.every((n) => config.cricket.numbers.includes(n)) &&
                config.cricket.numbers.length === 6
              }
              onClick={() =>
                onChange({
                  ...config,
                  cricket: { ...config.cricket, numbers: [...STANDARD_CRICKET_NUMBERS], includeBull: true },
                })
              }
            >
              Standard 15–20 + Bull
            </Chip>
            <Chip
              active={false}
              onClick={() => {
                const set = randomCricketSet(true);
                onChange({
                  ...config,
                  cricket: { ...config.cricket, numbers: set.numbers, includeBull: set.includeBull },
                });
              }}
            >
              Zufällig (6 Zahlen ± Bull)
            </Chip>
          </div>
          <div className="grid grid-cols-5 gap-2 sm:grid-cols-7">
            {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => {
              const on = config.cricket.numbers.includes(n);
              return (
                <button
                  key={n}
                  className={`min-h-touch rounded-2xl font-bold ${on ? "bg-amber-glow text-ink-950" : "bg-ink-700"}`}
                  onClick={() => {
                    const numbers = on
                      ? config.cricket.numbers.filter((x) => x !== n)
                      : [...config.cricket.numbers, n];
                    onChange({ ...config, cricket: { ...config.cricket, numbers } });
                  }}
                >
                  {n}
                </button>
              );
            })}
            <button
              className={`min-h-touch rounded-2xl font-bold ${config.cricket.includeBull ? "bg-crimson text-white" : "bg-ink-700"}`}
              onClick={() => onChange({ ...config, cricket: { ...config.cricket, includeBull: !config.cricket.includeBull } })}
            >
              Bull
            </button>
          </div>
          <h3 className="mb-3 mt-6 text-xs uppercase tracking-[0.25em] text-slate-400">Team-Modus</h3>
          <div className="mb-3 flex flex-wrap gap-2">
            {(["solo", "league", "team"] as const).map((mode) => (
              <Chip key={mode} active={(config.teamPlay ?? "solo") === mode} onClick={() => setTeamPlay(mode)}>
                {TEAM_PLAY_LABELS[mode]}
              </Chip>
            ))}
          </div>
          <p className="text-sm text-slate-400">{CRICKET_TEAM_PLAY_BLURBS[config.teamPlay ?? "solo"]}</p>
        </section>
      )}

      {config.gameType === "elimination" && (
        <section className="mb-6 rounded-3xl bg-ink-800 p-5">
          <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">Elimination</h2>
          <div className="mb-2 text-sm text-slate-400">Ziel-Score</div>
          <div className="mb-4 flex flex-nowrap items-center gap-2">
            {X01_PRESETS.map((s) => (
              <Chip
                key={s}
                active={config.elimination.target === s}
                onClick={() => onChange({ ...config, elimination: { ...config.elimination, target: s } })}
              >
                {s}
              </Chip>
            ))}
            <input
              type="number"
              min={2}
              max={10001}
              value={config.elimination.target}
              aria-label="Eigenes Ziel"
              title="Eigenes Ziel"
              onChange={(e) =>
                onChange({ ...config, elimination: { ...config.elimination, target: Number(e.target.value) || 301 } })
              }
              className="min-h-touch w-24 shrink-0 rounded-2xl bg-ink-950 px-3"
            />
          </div>
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <div className="mb-2 text-sm text-slate-400">In</div>
              <div className="flex flex-wrap gap-2">
                <Chip
                  active={config.elimination.inMode === "straight"}
                  onClick={() => onChange({ ...config, elimination: { ...config.elimination, inMode: "straight" } })}
                >
                  Straight In
                </Chip>
                <Chip
                  active={config.elimination.inMode === "double"}
                  onClick={() => onChange({ ...config, elimination: { ...config.elimination, inMode: "double" } })}
                >
                  Double In
                </Chip>
              </div>
            </div>
            <div>
              <div className="mb-2 text-sm text-slate-400">Out</div>
              <div className="flex flex-wrap gap-2">
                {(["straight", "double", "master"] as const).map((mode) => (
                  <Chip
                    key={mode}
                    active={config.elimination.outMode === mode}
                    onClick={() => onChange({ ...config, elimination: { ...config.elimination, outMode: mode } })}
                  >
                    {mode === "straight" ? "Straight Out" : mode === "double" ? "Double Out" : "Master Out"}
                  </Chip>
                ))}
              </div>
            </div>
          </div>
          <div className="mb-2 text-sm text-slate-400">Modus</div>
          <div className="mb-3 flex flex-wrap gap-2">
            <Chip
              active={!config.elimination.extreme}
              onClick={() => onChange({ ...config, elimination: { ...config.elimination, extreme: false } })}
            >
              Normal
            </Chip>
            <Chip
              active={config.elimination.extreme}
              onClick={() => onChange({ ...config, elimination: { ...config.elimination, extreme: true } })}
            >
              Extreme
            </Chip>
          </div>
          <p className="text-sm text-slate-400">
            Alle starten bei 0 und zählen hoch bis zum Ziel. Wer nach einem Dart genau den Score eines Gegners trifft,
            setzt diesen auf 0 zurück (Score 0 setzt niemanden zurück).
          </p>
          <p className="mt-2 text-sm text-slate-400">
            {config.elimination.extreme
              ? "Extreme: Überwerfen beendet die Aufnahme. Neuer Score = Ziel − Überhang (z. B. 298 + T20 bei 301 → 244). Kein Gewinn."
              : "Normal: Überwerfen oder ungültiger Out beendet die Aufnahme – Score wie zu Beginn der Aufnahme (wie x01-Bust)."}
          </p>
          <p className="mt-2 text-sm text-slate-400">Eingabe nur Dart für Dart – damit Knock-offs sofort greifen.</p>
        </section>
      )}

      {config.gameType === "clock" && (
        <section className="mb-6 rounded-3xl bg-ink-800 p-5">
          <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">Around the Clock</h2>
          <div className="flex flex-wrap gap-2">
            {(["any", "double", "triple"] as const).map((mode) => (
              <Chip
                key={mode}
                active={config.clock.requireMode === mode}
                onClick={() => onChange({ ...config, clock: { requireMode: mode } })}
              >
                {mode === "any" ? "Jedes Segment" : mode === "double" ? "Nur Doubles" : "Nur Triples"}
              </Chip>
            ))}
          </div>
          {config.clock.requireMode === "any" ? (
            <p className="mt-3 text-sm text-slate-400">
              Richtiges Doppel überspringt die nächste Zahl, richtiges Triple die nächsten zwei. Doppel 19 landet auf
              Bull; Triple 19 oder Doppel/Triple 20 beendet ohne Bull.
            </p>
          ) : (
            <p className="mt-3 text-sm text-slate-400">
              Nur das passende Segment zählt. Die Zahlen werden der Reihe nach gespielt, ohne Überspringen.
            </p>
          )}
        </section>
      )}

      {config.gameType === "shanghai" && (
        <section className="mb-6 rounded-3xl bg-ink-800 p-5">
          <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">Shanghai</h2>
          <label className="mb-4 block">
            <span className="text-sm text-slate-400">Runden (Standardmäßig 7)</span>
            <input
              type="number"
              min={1}
              max={20}
              value={config.shanghai.endNumber}
              onChange={(e) => onChange({ ...config, shanghai: { ...config.shanghai, endNumber: Number(e.target.value) || 7 } })}
              className="mt-1 min-h-touch w-full rounded-2xl bg-ink-950 px-4"
            />
          </label>
          <p className="mt-3 text-sm text-slate-400">
            Immer nach oben: nur die aktuelle Zahl zählt, danach die nächste (S6, T7, D8 in einer Aufnahme ist möglich).
            Nach 20 kommt Bull. Single + Double + Triple in einer Aufnahme (beliebige Zahlen der Folge) gewinnt sofort.
            Nach 7 Runden ohne Shanghai entscheidet die höchste Punktzahl – bei Gleichstand wird weitergespielt, bis
            jemand führt.
          </p>
        </section>
      )}

      <section className="mb-6 rounded-3xl bg-ink-800 p-5">
        <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">Spieler ({n})</h2>
        {teams.length > 0 && (
          <div className="mb-4">
            <div className="mb-2 text-sm text-slate-400">Welche Teams spielen?</div>
            <div className="mb-3 flex flex-wrap gap-2">
              {teams.map((team) => {
                const on = playingTeams.some((t) => t.id === team.id);
                return (
                  <Chip
                    key={team.id}
                    active={on}
                    onClick={() => {
                      const next = on
                        ? playingTeams.filter((t) => t.id !== team.id)
                        : [...playingTeams, { id: team.id, name: team.name }];
                      setTeamsPlaying(next);
                    }}
                  >
                    {team.name}
                  </Chip>
                );
              })}
            </div>
            {playingTeams.length > 0 && (
              <div className="mb-4 grid gap-3">
                {teams
                  .filter((team) => playingTeams.some((t) => t.id === team.id))
                  .map((team) => (
                    <div key={team.id} className="rounded-2xl bg-ink-950 p-3">
                      <div className="font-display text-xl">{team.name}</div>
                      <div className="mt-2 text-sm text-slate-300">
                        {(team.players ?? []).length
                          ? (team.players ?? []).map((p) => p.name).join(" · ")
                          : "Keine Spieler — in Admin hinzufügen"}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
        <div className="mb-4">
          {isTeamFormat(config) ? (
            <>
              <div className="mb-2 text-sm text-slate-400">Anzahl Teams</div>
              <div className="flex flex-wrap gap-2">
                {TEAM_COUNT_OPTIONS.map((count) => (
                  <Chip key={count} active={playingTeamCount(config) === count} onClick={() => setTeamCount(count)}>
                    {count}
                  </Chip>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 8 }, (_, i) => i + 1).map((count) => (
                <Chip key={count} active={n === count} onClick={() => setPlayers(count)}>
                  {count}
                </Chip>
              ))}
            </div>
          )}
        </div>
        {isTeamFormat(config) ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {Array.from({ length: playingTeamCount(config) }, (_, side) => {
              const heading = playingTeams[side]?.name ?? fallbackTeamLabel(side);
              const slots = [side * 2, side * 2 + 1];
              return (
                <div key={side} className="rounded-2xl bg-ink-950 p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="font-display text-xl">{heading}</div>
                    <Chip
                      active={teamSideStarts(config.firstThrowerIndex ?? 0, side)}
                      onClick={() => onChange({ ...config, firstThrowerIndex: side * 2 })}
                    >
                      Wirft zuerst
                    </Chip>
                  </div>
                  <div className="grid gap-2">
                    {slots.map((i) => (
                      <PlayerSlot
                        key={i}
                        index={i}
                        config={config}
                        players={players}
                        registry={registry}
                        teams={teams}
                        playingTeams={playingTeams}
                        onPick={pickPlayer}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid gap-2">
            {players.map((_, i) => (
              <PlayerSlot
                key={players[i]?.id || `slot-${i}`}
                index={i}
                config={config}
                players={players}
                registry={registry}
                teams={teams}
                playingTeams={playingTeams}
                onPick={pickPlayer}
                onFirst={popupOrder ? undefined : () => onChange({ ...config, firstThrowerIndex: i })}
              />
            ))}
          </div>
        )}
        <form
          className="mt-4 flex flex-wrap gap-2"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            setPlayerError(null);
            try {
              const result = await createPlayer(newName, apiBase, desktop, origin, newPassNr.trim() || null);
              if (!result.ok) {
                setPlayerError(result.error);
                return;
              }
              setRegistry((cur) =>
                [...cur, { ...result.player, createdAt: result.player.createdAt ?? Date.now() }].sort((a, b) =>
                  a.name.localeCompare(b.name, "de"),
                ),
              );
              const empty = players.findIndex((p) => !p.id);
              const nextPlayers = players.map((p, idx) =>
                idx === (empty >= 0 ? empty : 0) ? { id: result.player.id, name: result.player.name, teamId: result.player.teamId, teamName: result.player.teamName } : p,
              );
              onChange({ ...config, players: nextPlayers });
              setNewName("");
              setNewPassNr("");
            } catch {
              setPlayerError("Spieler konnte nicht angelegt werden.");
            }
          }}
        >
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Neuen Spieler anlegen"
            className="min-h-touch flex-1 rounded-2xl bg-ink-950 px-4"
            autoComplete="off"
            required={false}
          />
          <input
            value={newPassNr}
            onChange={(e) => setNewPassNr(e.target.value)}
            placeholder="Spielernummer/PassNr (optional)"
            className="min-h-touch w-44 rounded-2xl bg-ink-950 px-4"
            autoComplete="off"
            required={false}
          />
          <button type="submit" className="min-h-touch rounded-2xl bg-ink-700 px-4 font-bold" disabled={!newName.trim()}>
            Anlegen
          </button>
        </form>
        {playerError && <p className="mt-2 text-sm text-crimson">{playerError}</p>}
        {error && <p className="mt-2 text-sm text-crimson">{error}</p>}
        {!ready && (
          <p className="mt-2 text-sm text-slate-400">Bitte für jeden Platz einen Spieler zuweisen.</p>
        )}
      </section>

      {config.gameType !== "highscore" && (
      <section className="mb-6 rounded-3xl bg-ink-800 p-5">
        <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">Legs &amp; Sätze</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm text-slate-400">Legs zum Satzgewinn (First to)</span>
            <input
              type="number"
              min={1}
              max={21}
              value={config.legsToWinSet}
              onChange={(e) => onChange({ ...config, legsToWinSet: Number(e.target.value) || 1 })}
              className="mt-1 min-h-touch w-full rounded-2xl bg-ink-950 px-4"
            />
          </label>
          <label className="block">
            <span className="text-sm text-slate-400">Sätze zum Matchsieg (1 = nur Legs)</span>
            <input
              type="number"
              min={1}
              max={21}
              value={config.setsToWin}
              onChange={(e) => onChange({ ...config, setsToWin: Number(e.target.value) || 1 })}
              className="mt-1 min-h-touch w-full rounded-2xl bg-ink-950 px-4"
            />
          </label>
        </div>
        {config.gameType === "x01" && (
        <label className="mt-5 flex min-h-touch items-center gap-3 rounded-2xl bg-ink-950 px-4">
          <input
            type="checkbox"
            checked={config.bullUpLastLeg !== false}
            onChange={(e) => onChange({ ...config, bullUpLastLeg: e.target.checked })}
          />
          <span>Letztes Leg ausbullen</span>
        </label>
        )}
      </section>
      )}

      <section className="mb-6 rounded-3xl bg-ink-800 p-5">
        <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-slate-400">Eingabe</h2>
        <div className="mb-3 flex flex-wrap gap-2">
          <Chip
            active={dartPad}
            onClick={() => onChange({ ...config, inputSource: "pad" })}
          >
            Darteingabe
          </Chip>
          <Chip
            active={!dartPad}
            onClick={() => onChange({ ...config, inputSource: "autodarts", inputMode: "single" })}
          >
            Autodarts
          </Chip>
        </div>
        {dartPad && config.gameType === "x01" && (
          <div className="mb-3 flex flex-wrap gap-2">
            <Chip active={config.inputMode === "single"} onClick={() => onChange({ ...config, inputMode: "single" })}>
              Einzel
            </Chip>
            <Chip active={config.inputMode === "total"} onClick={() => onChange({ ...config, inputMode: "total" })}>
              Gesamteingabe
            </Chip>
          </div>
        )}
        {!dartPad && (
          <>
            <label className="block text-sm text-slate-400">Board Manager URL</label>
            <input
              value={config.autodartsUrl}
              onChange={(e) => {
                const autodartsUrl = e.target.value;
                onChange({ ...config, autodartsUrl });
                void desktop?.saveSettings?.({ autodartsUrl });
              }}
              placeholder="http://127.0.0.1:3180"
              className="mt-1 min-h-touch w-full rounded-2xl bg-ink-950 px-4 outline-none ring-amber-glow/40 focus:ring"
            />
            <p className="mt-2 text-xs text-slate-500">
              Autodarts ist optional. Die Desktop-App (Windows / Pi) liest den Board Manager im selben Netz. Darteingabe
              bleibt immer als Korrektur da.
            </p>
          </>
        )}
        {dartPad && (
          <p className="text-xs text-slate-500">
            {config.gameType === "x01" && config.inputMode === "total"
              ? "Aufnahme als 3-Dart-Summe eingeben."
              : "Treffer Dart für Dart. Autodarts kannst du jederzeit dazuwählen."}
          </p>
        )}
      </section>


      <button
        className="mb-8 min-h-kiosk w-full rounded-3xl bg-amber-glow font-display text-3xl text-ink-950 disabled:opacity-50"
        disabled={!ready}
        onClick={requestStart}
      >
        Match starten
      </button>
      {orderOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-6">
          <ThrowOrderPicker
            players={players}
            sequence={startSequence}
            onChange={setStartSequence}
            confirmLabel="Match starten"
            onConfirm={() => {
              onChange(applyThrowSequence(config, startSequence));
              setOrderOpen(false);
              onStart();
            }}
            onCancel={() => setOrderOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
